import { AppointmentAvailabilityRuleDto } from '../../interfaces/appointments';

export const DEFAULT_CALENDAR_START_HOUR = 8;
export const DEFAULT_CALENDAR_END_HOUR = 20;

export type CalendarHourRange = {
  startHour: number;
  endHour: number;
};

type RangeAppointment = {
  date: string;
  time: string;
  durationMinutes: number;
};

export const parseClockToMinutes = (time: string): number | null => {
  const [hoursPart, minutesPart] = (time ?? '').split(':');
  const hours = Number(hoursPart);
  const minutes = Number(minutesPart);
  if (!hoursPart || !minutesPart || !Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return (hours * 60) + minutes;
};

const weekdayOfIsoDate = (isoDate: string): number => new Date(`${isoDate}T00:00:00`).getDay();

const ruleAppliesToDate = (rule: AppointmentAvailabilityRuleDto, isoDate: string): boolean => {
  if (!rule || rule.isActive !== true || typeof rule.startTime !== 'string' || typeof rule.endTime !== 'string') {
    return false;
  }
  if (rule.weekday !== weekdayOfIsoDate(isoDate)) {
    return false;
  }
  const startDate = (rule.startDate ?? '').slice(0, 10);
  const endDate = (rule.endDate ?? '').slice(0, 10);
  if (startDate && isoDate < startDate) {
    return false;
  }
  if (endDate && isoDate > endDate) {
    return false;
  }
  return true;
};

/**
 * Computes the hour-aligned time span the calendar must show for the given days. It is never smaller than
 * the default span, and expands to cover every active availability rule and every booking on those days.
 */
export const computeCalendarHourRange = (
  isoDates: string[],
  appointments: RangeAppointment[],
  rules: AppointmentAvailabilityRuleDto[],
  defaultStartHour: number = DEFAULT_CALENDAR_START_HOUR,
  defaultEndHour: number = DEFAULT_CALENDAR_END_HOUR,
): CalendarHourRange => {
  let startMinutes = defaultStartHour * 60;
  let endMinutes = defaultEndHour * 60;
  const dates = new Set(isoDates);

  const include = (from: number | null, to: number | null) => {
    if (from === null || to === null || to <= from) {
      return;
    }
    startMinutes = Math.min(startMinutes, from);
    endMinutes = Math.max(endMinutes, to);
  };

  isoDates.forEach((isoDate) => {
    rules.forEach((rule) => {
      if (ruleAppliesToDate(rule, isoDate)) {
        include(parseClockToMinutes(rule.startTime), parseClockToMinutes(rule.endTime));
      }
    });
  });

  appointments.forEach((appointment) => {
    if (!dates.has(appointment.date)) {
      return;
    }
    const start = parseClockToMinutes(appointment.time);
    if (start !== null) {
      include(start, start + Math.max(appointment.durationMinutes || 0, 1));
    }
  });

  return {
    startHour: Math.max(0, Math.floor(startMinutes / 60)),
    endHour: Math.min(24, Math.ceil(endMinutes / 60)),
  };
};
